import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { docsNav } from './docsNav';

function sectionContains(section, slug) {
  return (section.pages || []).some((page) => page.slug === slug)
    || (section.groups || []).some((group) => group.pages.some((page) => page.slug === slug));
}

function groupContains(group, slug) {
  return group.pages.some((page) => page.slug === slug);
}

function Chevron({ open }) {
  return (
    <svg
      className={`h-3 w-3 shrink-0 transition-transform ${open ? 'rotate-0' : '-rotate-90'}`}
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
    </svg>
  );
}

function PageLink({ page, active, nested = false }) {
  return (
    <Link
      to={`/docs/${page.slug}`}
      className={`block rounded-lg py-1.5 text-sm leading-snug transition-colors ${nested ? 'pl-6 pr-3' : 'px-3'} ${
        active
          ? 'bg-gray-100 font-medium text-gray-900 dark:bg-brand-50/10 dark:text-brand-50'
          : 'text-gray-500 hover:bg-gray-50 hover:text-gray-900 dark:text-brand-500 dark:hover:bg-brand-50/5 dark:hover:text-brand-200'
      }`}
    >
      {page.title}
    </Link>
  );
}

export default function DocsLeftNav() {
  const { slug } = useParams();
  const activeSection = docsNav.find((section) => sectionContains(section, slug));
  const activeGroup = activeSection?.groups?.find((group) => groupContains(group, slug));

  const [openSections, setOpenSections] = useState(() => ({
    [activeSection?.slug || docsNav[0].slug]: true,
  }));
  const [openGroups, setOpenGroups] = useState(() => ({
    [activeGroup?.slug]: Boolean(activeGroup),
  }));

  useEffect(() => {
    if (activeSection?.slug) {
      setOpenSections((current) => ({ ...current, [activeSection.slug]: true }));
    }
    if (activeGroup?.slug) {
      setOpenGroups((current) => ({ ...current, [activeGroup.slug]: true }));
    }
  }, [activeSection?.slug, activeGroup?.slug]);

  return (
    <nav className="px-5 pb-12 pt-8">
      {docsNav.map((section) => {
        const hasActiveChild = sectionContains(section, slug);
        const isOpen = section.alwaysOpen || openSections[section.slug];
        const headingClass = `flex w-full items-center justify-between text-left text-[10px] font-bold uppercase tracking-widest ${
          hasActiveChild
            ? 'text-gray-700 dark:text-brand-300'
            : 'text-gray-400 dark:text-brand-600'
        }`;

        return (
          <div key={section.slug} className="mb-6">
            {section.alwaysOpen ? (
              <div className={headingClass}><span>{section.title}</span></div>
            ) : (
              <button
                type="button"
                onClick={() => setOpenSections((current) => ({
                  ...current,
                  [section.slug]: !current[section.slug],
                }))}
                className={`${headingClass} hover:text-gray-600 dark:hover:text-brand-400`}
              >
                <span>{section.title}</span>
                <Chevron open={isOpen} />
              </button>
            )}

            {isOpen && (
              <div className="mt-2 space-y-0.5">
                {(section.pages || []).map((page) => (
                  <PageLink key={page.slug} page={page} active={page.slug === slug} />
                ))}

                {(section.groups || []).map((group) => {
                  const isGroupOpen = openGroups[group.slug];
                  const hasActivePage = groupContains(group, slug);
                  return (
                    <div key={group.slug} className="pt-1">
                      <button
                        type="button"
                        onClick={() => setOpenGroups((current) => ({
                          ...current,
                          [group.slug]: !current[group.slug],
                        }))}
                        className={`flex w-full items-center justify-between rounded-lg px-3 py-1.5 text-left text-sm font-semibold transition-colors ${
                          hasActivePage
                            ? 'text-gray-900 dark:text-brand-100'
                            : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900 dark:text-brand-400 dark:hover:bg-brand-50/5 dark:hover:text-brand-200'
                        }`}
                      >
                        <span>{group.title}</span>
                        <Chevron open={isGroupOpen} />
                      </button>
                      {isGroupOpen && (
                        <div className="mt-0.5 border-l border-gray-200 dark:border-brand-50/10">
                          {group.pages.map((page) => (
                            <PageLink key={page.slug} page={page} active={page.slug === slug} nested />
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </nav>
  );
}
